import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";
import { S25DeleteModule } from "../../../../../s25-delete/s25.delete.module";
import { S25EditableModule } from "../../../../../s25-editable/s25.editable.module";
import { S25CheckboxModule } from "../../../../../s25-checkbox/s25.checkbox.module";
import { S25IconComponent } from "../../../../../s25-icon/s25.icon.component";
import { S25ItemComponent } from "../../../../../s25-item/s25.item.component";
import { S25InfoMessageModule } from "../../../../../s25-info-message/s25.info.message.module";
import { Item } from "../../../../../../pojo/Item";
import { Fls } from "../../../../../../pojo/Fls";
import { S25RmReservation, rsrvType } from "../../../../ReservationI";
import { WaitlistEntry } from "../../../../../../pojo/Waitlist";
import { layoutSelectOptionLabel, locationDefaultCapacity, OccurrenceDeleteWarning } from "./occurrence.details.shared";

@Component({
    selector: "occurrence-location-row",
    template: `<div class="location {{ rsrvType() }}">
            @if (waitlistEvents()?.length > 0 && locationWaitlistEnabled()) {
                <div class="ngBold">
                    {{ waitlistEvents()!.length > 1 ? "Waitlist Events:" : "Waitlist Event:" }}
                    @for (group of waitlistGroups(); track group.groupKey) {
                        <button
                            [disabled]="!hasWaitlistFls()"
                            class="aw-button aw-button--outline c-margin-bottom--quarter"
                            (click)="
                                openWaitlistDetail.emit({ waitlistId: group.waitlistId, groupKey: group.groupKey })
                            "
                        >
                            Waitlist Details
                            <s25-ng-icon [type]="'queue'" [ariaLabel]="false" />
                        </button>
                    }
                </div>

                <div class="c-margin-left--half c-margin-bottom--half">
                    @for (event of waitlistEvents(); track event.eventId) {
                        <div class="waitlist-row">
                            <s25-item
                                [itemId]="event.eventId"
                                [itemTypeId]="Item.Ids.Event"
                                [includeTypeIcon]="true"
                                [typeIconClass]="'icon-event-pending'"
                            ></s25-item>
                        </div>
                    }
                </div>
            }
            <div class="no-break-content">
                <s25-item
                    [itemId]="location().itemId"
                    [itemTypeId]="Item.Ids.Location"
                    [itemName]="location().itemName"
                    [includeTypeIcon]="true"
                    [typeIconClass]="rsrvType() === 'requested' || rsrvType() === 'draft' ? 'icon-space-pending' : ''"
                ></s25-item>
                @if (location().hasPerm) {
                    <div class="occurrence-shared-control">
                        <span>Shared</span>
                        <s25-ng-info-message [isPopover]="true" [customLabel]="'Shared'">
                            When checked, this location can be double-booked: other events may use the same space at the
                            same time. When unchecked, the space is reserved for this event only.
                        </s25-ng-info-message>
                        <s25-ng-checkbox
                            [ariaLabel]="'Shared location for ' + location().itemName"
                            [value]="location().isShare"
                            [disabled]="!canEdit() || fls()?.EVENT_SHARE !== 'F'"
                            (valueChange)="sharedChange.emit($event)"
                        />
                    </div>
                }
                @if (canEdit() && (location().hasPerm || isModalSaveMode())) {
                    <div>
                        <button
                            class="aw-button aw-button--danger--transparent"
                            (click)="confirmDelete.emit()"
                            [disabled]="
                                deleteWarning().index === index() &&
                                deleteWarning().objType === 'space' &&
                                deleteWarning().rsrvType === rsrvType()
                            "
                        >
                            {{ rsrvType() === "requested" ? "Cancel Request" : "Remove" }}
                        </button>
                        @if (showConflict()) {
                            <button type="button" class="c-textButton ngRed" (click)="openSpaceConflict.emit()">
                                Conflict
                            </button>
                        }
                    </div>
                }
            </div>
            @if (
                deleteWarning().index === index() &&
                deleteWarning().objType === "space" &&
                deleteWarning().rsrvType === rsrvType()
            ) {
                <s25-ng-delete-inline
                    [confirmMsg]="deleteWarning().msg"
                    [listItems]="deleteWarning().listItems"
                    (onConfirm)="deleteConfirm.emit($event)"
                />
            } @else {
                <div class="location-details">
                    @if (location().locationLayout?.length > 0 && canEdit() && location().hasPerm) {
                        <label>
                            Layout:
                            <span>
                                <select
                                    class="cn-form__control"
                                    id="layout{{ location().layout?.itemId }}"
                                    (change)="layoutChange.emit($event)"
                                >
                                    @if ($safeNavigationMigration(location()?.layout?.itemName) === undefined) {
                                        <option value="" selected></option>
                                    }
                                    @for (layout of location().locationLayout; track layout.layout_id) {
                                        <option
                                            [value]="layout.layout_id"
                                            [selected]="layout.layout_id === location().layout.itemId"
                                        >
                                            {{ layoutOptionLabel(layout) }}
                                        </option>
                                    }
                                </select>
                            </span>
                        </label>
                    }

                    @if (location().hasPerm) {
                        <div class="layout-capacity">
                            <span class="ngBold">Default Capacity: </span>{{ defaultCapacity() }}
                        </div>
                        <label class="layout-attendance">
                            Attendance:
                            <s25-ng-editable-number
                                [val]="location().attendance"
                                [readOnly]="!canEdit() || !location().hasPerm"
                                [hasCommit]="true"
                                [hasCancelButton]="true"
                                [hasCommitButton]="true"
                                commitButtonText="Save"
                                cancelButtonText="Cancel"
                                (valChange)="attendanceChange.emit($event)"
                            >
                            </s25-ng-editable-number>
                        </label>
                    }
                    @if ((!canEdit() || !location().hasPerm) && location()?.layout?.itemName) {
                        Layout: {{ location().layout.itemName }}
                    }

                    @if (location().hasPerm || isModalSaveMode()) {
                        <div [class.c-margin-top--single]="editMode()">
                            <label>
                                Instructions:
                                <s25-ng-editable-textarea
                                    [(val)]="location().instructions"
                                    [readOnly]="!canEdit() || !(location().hasPerm || isModalSaveMode())"
                                    placeholder="Add instructions"
                                    [hasCommit]="!isModalSaveMode()"
                                    [hasCancelButton]="!isModalSaveMode()"
                                    [hasCommitButton]="!isModalSaveMode()"
                                    [renderHtml]="true"
                                    commitButtonText="Save"
                                    cancelButtonText="Cancel"
                                    (valChange)="textareaChange.emit($event)"
                                />
                            </label>
                        </div>
                    }
                </div>
            }
        </div>
        @if (location().assignBuffer?.bufferWarningMsg && location().assignBuffer?.bufferWarningMsg !== " ") {
            <section class="cn-alert cn-alert--danger">
                {{ location().assignBuffer.bufferWarningMsg }}
            </section>
        }`,
    styles: `
        :host {
            display: block;
            width: 100%;
        }

        .location {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            width: 100%;
        }

        .no-break-content {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 12px;
            width: 100%;
        }

        .location-details {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
            width: 100%;
            margin-top: 0.5rem;
        }

        .layout-attendance {
            width: 100%;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        S25ItemComponent,
        S25CheckboxModule,
        S25InfoMessageModule,
        S25IconComponent,
        S25DeleteModule,
        S25EditableModule,
    ],
})
export class OccurrenceLocationRowComponent {
    readonly location = input.required<S25RmReservation>();
    readonly rsrvType = input.required<rsrvType>();
    readonly index = input.required<number>();
    readonly canEdit = input<boolean>();
    readonly editMode = input<boolean>();
    readonly isModalSaveMode = input(false);
    readonly showConflict = input(false);
    readonly deleteWarning = input.required<OccurrenceDeleteWarning>();
    readonly fls = input<Fls>();
    readonly waitlistEvents = input<{ eventId: number }[]>();
    readonly waitlistGroups = input<WaitlistEntry[]>([]);
    readonly locationWaitlistEnabled = input(false);
    readonly hasWaitlistFls = input(false);

    readonly sharedChange = output<boolean>();
    readonly confirmDelete = output<void>();
    readonly openSpaceConflict = output<void>();
    readonly deleteConfirm = output<boolean>();
    readonly layoutChange = output<Event>();
    readonly attendanceChange = output<number>();
    readonly textareaChange = output<string>();
    readonly openWaitlistDetail = output<{ waitlistId?: number | null; groupKey?: string | null }>();

    protected readonly defaultCapacity = computed(() => locationDefaultCapacity(this.location()));
    protected readonly Item = Item;
    protected readonly layoutOptionLabel = layoutSelectOptionLabel;
}
